import {
  Box,
  Card,
  chakra,
  Flex,
  FormatNumber,
  HStack,
  Icon,
  Image,
  Show,
  SimpleGrid,
  Skeleton,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { animate, motion, useMotionValue } from "motion/react";
import { useEffect, useState } from "react";
import { BiSolidDroplet } from "react-icons/bi";
import { FiArrowUpRight } from "react-icons/fi";
import {
  PiPuzzlePieceFill,
  PiSealCheckFill,
  PiSparkleFill,
} from "react-icons/pi";
import useMeasure from "react-use-measure";

import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { RoundedBadge } from "@/components/rounded-badge";
import {
  capitalizeFirstLetter,
  getIconPathFromAppId,
  getIconPathFromTokenSymbol,
} from "@/utils";

const BADGES_METADATA = {
  audited: {
    icon: <PiSealCheckFill />,
    name: "Audited",
  },
  liquid: {
    icon: <BiSolidDroplet />,
    name: "Liquid",
  },
  composable: {
    icon: <PiPuzzlePieceFill />,
    name: "Composable",
  },
  incentivized: {
    icon: <PiSparkleFill />,
    name: "Incentivized",
  },
};

const MotionBox = chakra(motion.div);

export const BadgeContent = ({
  badgeContent,
  apy,
  apyLoading = false,
  tvl,
}: {
  badgeContent: TBadgeContent;
  apy?: string | number | undefined;
  apyLoading?: boolean;
  tvl?: string;
}) => {
  const { audited, liquid, composable, incentivized } = badgeContent;
  const [selectedCategory, setSelectedCategory] = useState<
    keyof typeof BADGES_METADATA | undefined
  >();
  return (
    <VStack
      gap={3}
      flex={1}
      w="full"
      fontSize="sm"
      fontWeight="500"
      onMouseLeave={() => setSelectedCategory(undefined)}
    >
      <HStack gap={2} w="full" lgDown={{ display: "none" }}>
        {Object.keys(badgeContent).map((badgeName) => {
          const name = badgeName as keyof typeof BADGES_METADATA;
          const badge = BADGES_METADATA[name];
          return (
            <RoundedBadge
              key={badge.name}
              variant="subtle"
              color={selectedCategory === badgeName ? "fg" : "fg.muted"}
              h={9}
              bgColor={
                selectedCategory === badgeName ? "bg.emphasized" : "bg.subtle"
              }
              onMouseEnter={() => setSelectedCategory(name)}
            >
              <Icon>{badge.icon}</Icon>
              {badge.name}
            </RoundedBadge>
          );
        })}
      </HStack>
      <Flex w="full" h="150px" flexDirection="column">
        {selectedCategory ? (
          <>
            <Show when={selectedCategory === "audited"}>
              {audited && <AuditInfo {...(audited ?? {})} />}
            </Show>
            <Show when={selectedCategory === "liquid"}>
              {liquid && <LiquidInfo {...liquid} />}
            </Show>
            <Show when={selectedCategory === "composable"}>
              {composable && <ComposableInfo {...composable} />}
            </Show>
            <Show when={selectedCategory === "incentivized"}>
              {incentivized && <IncentivizedInfo {...incentivized} />}
            </Show>
          </>
        ) : (
          <>
            <SimpleGrid columns={2} w="full" marginTop="auto">
              <Stat label="APY" value={apy} loading={apyLoading} />
              <Stat label="TVL" value={tvl} />
            </SimpleGrid>
          </>
        )}
      </Flex>
    </VStack>
  );
};

const Stat = ({
  label,
  value,
  loading = false,
}: {
  label: string | undefined;
  value: string | number | undefined;
  loading?: boolean;
}) => {
  if (!loading && !value) return null;
  return (
    <Box>
      <Text fontSize="sm" color="fg.muted" mb={1}>
        {label}
      </Text>
      {loading ? (
        <Skeleton w={20} h={9} />
      ) : (
        <Text fontSize="2xl" fontWeight="700" fontFamily="heading">
          {typeof value === "number" ? (
            <FormatNumber value={value} style="percent" />
          ) : (
            value
          )}
        </Text>
      )}
    </Box>
  );
};

const AuditInfo = (props: TBadgeContent["audited"]) => {
  return (
    <VStack h="full" w="full" alignItems="stretch" minH="148px">
      {props?.auditInfo && (
        <AuditCard
          {...props.auditInfo}
          type="audit"
          fullSize={!props.diligenceInfo}
        />
      )}
      {props?.diligenceInfo && (
        <AuditCard
          {...props.diligenceInfo}
          type="diligence"
          fullSize={!props.auditInfo}
        />
      )}
    </VStack>
  );
};

const AuditCard = ({
  type,
  names,
  href,
  fullSize,
}: {
  type: "audit" | "diligence";
  names: AuditCompanyNames[] | DiligenceReporterNames[];
  fullSize: boolean;
  href: string;
}) => {
  return (
    <HoverCardWithLink
      href={href}
      showHoverIcon={false}
      cardRootStyles={{
        w: "full",
        borderRadius: "xl",
        bgColor: "bg.subtle",
        p: 3,
        flex: 1,
        h: "full",
      }}
      linkProps={{
        h: fullSize ? "full" : "initial",
        flex: 1,
      }}
    >
      <Icon
        as={FiArrowUpRight}
        boxSize={4}
        top={2}
        right={2}
        position="absolute"
      />
      <Text>
        {type === "diligence"
          ? "Expert diligence reports"
          : "Third-party audits and security"}
      </Text>
      {fullSize && (
        <Text color="fg.muted" maxW="70%">
          Best practice security measures with smart contracts audited and
          certified.
        </Text>
      )}
      <Spacer flex={1} />
      <HStack>
        {names.map((name) => (
          <Image key={name} src={getIconPathFromAuditorName(name)} />
        ))}
      </HStack>
    </HoverCardWithLink>
  );
};

const LiquidInfo = (props: TBadgeContent["liquid"]) => {
  if (!props) return null;
  return (
    <Card.Root
      flex={1}
      w="full"
      borderRadius="xl"
      bgColor="bg.subtle"
      minH="148px"
    >
      <Card.Body p={3}>
        <VStack alignItems="start" h="full">
          <Text>Deep liquidity</Text>
          <Text color="fg.muted" maxW="80%">
            Deep liquidity to support redemptions back to stables instantly.
          </Text>
          <Spacer flex={1} />
          <HStack>
            {props.assets.map((asset) => (
              <RoundedBadge h={9} key={asset}>
                <Image src={getIconPathFromTokenSymbol(asset)} boxSize={6} />
                {asset}
              </RoundedBadge>
            ))}
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
const ComposableInfo = (props: TBadgeContent["composable"]) => {
  const [ref, { width }] = useMeasure();
  const [containerRef, { width: containerWidth }] = useMeasure();
  const [maxW, setMaxW] = useState(0);
  // Freeze the first measured width so the animated content cannot resize its viewport.
  /* oxlint-disable react/set-state-in-effect */
  useEffect(() => {
    if (!maxW) setMaxW(containerWidth);
  }, [containerWidth, maxW]);
  /* oxlint-enable react/set-state-in-effect */
  const xTranslate = useMotionValue(0);
  const hasOverflow = width > containerWidth;
  useEffect(() => {
    if (!width || !containerWidth) return;

    const finalPosition = -width / 2 - 8;
    const controls = animate(xTranslate, [0, finalPosition], {
      ease: "linear",
      duration: 25,
      repeat: Infinity,
      repeatType: "loop",
      repeatDelay: 0,
    });

    return () => {
      controls.stop();
      controls.complete();
    };
  }, [width, xTranslate, containerWidth]);
  if (!props) return null;
  return (
    <Card.Root
      flex={1}
      w="full"
      overflow="hidden"
      borderRadius="xl"
      bgColor="bg.subtle"
      minH="148px"
    >
      <Card.Body p={3}>
        <VStack alignItems="start" h="full" ref={containerRef}>
          <Text>Fully composable</Text>
          <Text color="fg.muted">
            Swap, lend, borrow, or loop seamlessly to maximize APYs
          </Text>
          <Spacer flex={1} />
          <Box w="full" overflow="auto" maxW={maxW}>
            <MotionBox
              gap={2}
              display="flex"
              minW="max-content"
              whiteSpace="nowrap"
              transition="transform 0.3s"
              ref={ref}
              style={hasOverflow ? { x: xTranslate } : {}}
            >
              {/* if no overflow, do not repeat items */}
              {props.apps
                .concat(hasOverflow ? props.apps : [])
                .map((app, i) => (
                  <RoundedBadge key={`${app}-${i}`} h={9}>
                    <Image
                      src={getIconPathFromAppId(app)}
                      boxSize={6}
                      borderRadius="full"
                    />
                    {capitalizeFirstLetter(app)}
                  </RoundedBadge>
                ))}
            </MotionBox>
          </Box>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

const IncentivizedInfo = (props: TBadgeContent["incentivized"]) => {
  if (!props) return null;
  return (
    <VStack w="full" flex={1}>
      {props.incentives.map((incentive) => (
        <Card.Root
          key={incentive}
          flex={1}
          w="full"
          borderRadius="xl"
          bgColor="bg.subtle"
        >
          <Card.Body p={3}>
            <Stack
              gap={3}
              h="full"
              direction={props.incentives.length > 1 ? "row" : "column"}
              justifyContent={
                props.incentives.length > 1 ? "inherit" : "space-evenly"
              }
            >
              <Image
                src={
                  incentive === "pp"
                    ? "/assets/pp-circle.svg"
                    : getIconPathFromTokenSymbol("plume")
                }
                boxSize="34px"
              />
              <VStack alignItems="start" gap={0}>
                <Text>
                  {incentive === "pp"
                    ? "Earn Plume Points"
                    : "Earn PLUME Incentives"}
                </Text>
                <Text color="fg.muted">
                  {incentive === "pp"
                    ? "For a share in Plume's Airdrop Season 2"
                    : "As part of Royco or Merkl campaigns"}
                </Text>
              </VStack>
            </Stack>
          </Card.Body>
        </Card.Root>
      ))}
    </VStack>
  );
};
export type AuditCompanyNames =
  | "hacken"
  | "slowmist"
  | "0xmacro"
  | "pashov"
  | "spearbit"
  | "shieldify"
  | "cantina"
  | "nethermind"
  | "zellic"
  | "blocksec";
export type TBadgeContent = {
  audited?: {
    auditInfo: {
      names: AuditCompanyNames[];
      href: string;
    };
    diligenceInfo?: {
      names: DiligenceReporterNames[];
      href: string;
    };
  };
  liquid?: {
    assets: string[];
  };
  composable?: {
    apps: string[];
  };
  incentivized: {
    incentives: ("pp" | "plume")[];
  };
};
export type DiligenceReporterNames = "cicada";

const getIconPathFromAuditorName = (name: string) => {
  return `/assets/home/logos/${name.toLowerCase()}.svg`;
};
